import { useEffect, useMemo, useState } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Input,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular, Search20Regular } from '@fluentui/react-icons';

import { TTrainingEmployee } from '../types';

type TProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  employees: TTrainingEmployee[];
  onApply: (employees: TTrainingEmployee[]) => void;
};

const useStyles = makeStyles({
  surface: {
    maxWidth: '520px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  intro: {
    color: tokens.colorNeutralForeground3,
  },
  selectAllRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  count: {
    color: tokens.colorNeutralForeground3,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '300px',
    overflowY: 'auto',
  },
  row: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXXS}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  rowText: {
    display: 'inline-flex',
    flexDirection: 'column',
  },
  function: {
    color: tokens.colorNeutralForeground3,
  },
  empty: {
    padding: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground3,
  },
});

// A small searchable list for reviewing/editing the auto-identified training
// audience. Each employee shows their name + function and an "included" checkbox;
// a select-all toggles every row currently matching the search. Works on a local
// draft so changes only take effect when the user clicks Done.
const AffectedEmployeesDialog = ({ isOpen, setIsOpen, employees, onApply }: TProps) => {
  const classes = useStyles();
  const [draft, setDraft] = useState<TTrainingEmployee[]>(employees);
  const [search, setSearch] = useState('');

  // Snapshot the incoming selection each time the dialog opens (dialogs stay mounted).
  useEffect(() => {
    if (isOpen) {
      setDraft(employees);
      setSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snapshot on open only
  }, [isOpen]);

  const filtered = useMemo(() => {
    const normalized = search.trim().toLocaleLowerCase();
    if (!normalized) return draft;
    return draft.filter((employee) => `${employee.name} ${employee.function}`.toLocaleLowerCase().includes(normalized));
  }, [draft, search]);

  const includedCount = draft.filter((employee) => employee.included).length;
  const allFilteredIncluded = filtered.length > 0 && filtered.every((employee) => employee.included);
  const someFilteredIncluded = filtered.some((employee) => employee.included);

  const toggle = (id: string, checked: boolean) =>
    setDraft((prev) => prev.map((employee) => (employee.id === id ? { ...employee, included: checked } : employee)));

  const toggleAll = (checked: boolean) => {
    const filteredIds = new Set(filtered.map((employee) => employee.id));
    setDraft((prev) =>
      prev.map((employee) => (filteredIds.has(employee.id) ? { ...employee, included: checked } : employee))
    );
  };

  const handleApply = () => {
    onApply(draft);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => setIsOpen(data.open)}>
      <DialogSurface className={classes.surface}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            Affected employees
          </DialogTitle>
          <DialogContent className={classes.content}>
            <Caption1 className={classes.intro}>
              Automatically identified from their assigned functions. Review and adjust who needs the training.
            </Caption1>

            <Input
              contentBefore={<Search20Regular />}
              placeholder="Search by name or function"
              value={search}
              onChange={(_, data) => setSearch(data.value)}
            />

            <div className={classes.selectAllRow}>
              <Checkbox
                checked={allFilteredIncluded ? true : someFilteredIncluded ? 'mixed' : false}
                onChange={(_, data) => toggleAll(data.checked === true)}
                label="Select all"
              />
              <Caption1 className={classes.count}>
                {includedCount} of {draft.length} included
              </Caption1>
            </div>

            <div className={classes.list}>
              {filtered.map((employee) => (
                <div key={employee.id} className={classes.row}>
                  <Checkbox
                    checked={Boolean(employee.included)}
                    onChange={(_, data) => toggle(employee.id, data.checked === true)}
                    label={
                      <span className={classes.rowText}>
                        <Body1>{employee.name}</Body1>
                        <Caption1 className={classes.function}>{employee.function}</Caption1>
                      </span>
                    }
                  />
                </div>
              ))}
              {filtered.length === 0 && <Caption1 className={classes.empty}>No employees match your search.</Caption1>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={handleApply}>
              Done
            </Button>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default AffectedEmployeesDialog;
